import { useState } from 'react';

import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Link,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import { AddChip, HeaderCard, TwoColumn } from '@/cam-admin/control-panel/bits';
import {
  ASSIGNED_CASE_MANAGER,
  CARE_CIRCLE,
  CASE_MANAGERS,
  INITIAL_WATCHERS,
  INTAKE,
  SEED_CALLS,
  initials,
} from '@/cam-admin/control-panel/fixtures';
import {
  INTAKE_ACTIONS,
  PROFILE_DOCS,
} from '@/karehero-app/tell-us-once/intake/content';
import { CALCULATOR_RESULT } from '@/karehero-app/tell-us-once/benefits/BenefitsCalculator.content';

/**
 * A labelled fact on the recipient card.
 *
 * Split from KeyValue because conditions are chips rather than a line of text,
 * and they should be labelled the same way as the age beside them. The label is
 * the thing being shared; what sits under it is the caller's.
 */
const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{ display: 'block' }}
      variant='caption'
      color='text.secondary'
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const KeyValue = ({ label, value }: { label: string; value: string }) => (
  <Field label={label}>
    <Typography variant='body2'>{value}</Typography>
  </Field>
);

// A calendar tab (day over month) for the upcoming call, from a 'Wed 17 Jun'
// date string. 52px square, coral, bold — matching the Figma tile.
const DateChip = ({ date }: { date: string }) => {
  const [, day, month] = date.split(' ');
  return (
    <Box
      sx={{
        bgcolor: 'custom.coral.100',
        borderRadius: `${radius.md}px`,
        width: 52,
        height: 52,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography variant='caption' color='custom.coral.900'>
        {day}
      </Typography>
      <Typography variant='caption' color='custom.coral.900'>
        {(month ?? '').toUpperCase()}
      </Typography>
    </Box>
  );
};

// The Overview tab: at a glance, who this circle is for (recipient, carers,
// the next call) on the left, and who owns it on the KareHero side (case
// manager, watchers, the linked circles) on the right. Every card is a
// HeaderCard — a header split from its body by a full-width stroke.
const OverviewTab = ({
  onToast,
  intake,
  onOpenCarer,
  onViewCalls,
  onOpenCall,
  onEditProfile,
}: {
  onToast: (m: string) => void;
  /** Opens the carer's details over the frame. See CarerDrawer. */
  onOpenCarer?: (name: string) => void;
  /**
   * Switches to the Calls tab. Both View calls buttons take it, because they
   * are the same request asked from two cards.
   */
  onViewCalls?: () => void;
  /** Opens the recipient's record for editing. See ProfileDrawer. */
  onEditProfile?: () => void;
  /** Opens one call, which is the Calls tab with its drawer over it. */
  onOpenCall?: (id: string) => void;
  /**
   * Whether a Tell Us Once intake sits behind this circle, and whether the
   * family consented to it being read.
   *
   * Opt-in, the way the paywall shortlist is: the intake is still being
   * designed, so it should not appear on the CAM Admin pages until it is
   * decided. The Tell Us Once flows page passes it and nothing else changes.
   */
  intake?: 'consented' | 'declined';
}) => {
  const mock = (what: string) => () =>
    onToast(`${what} is mocked in the prototype`);

  const upNext = SEED_CALLS.find((c) => c.upcoming);

  // Who owns this circle on our side. Reassignable from the card action.
  const [caseManager, setCaseManager] = useState(ASSIGNED_CASE_MANAGER);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const assigned = CASE_MANAGERS.find((m) => m.name === caseManager);

  // Staff following this circle. Add from the card action, remove via the chip.
  const [watchers, setWatchers] = useState<string[]>(INITIAL_WATCHERS);
  const [watcherAnchor, setWatcherAnchor] = useState<HTMLElement | null>(null);
  const canWatch = CASE_MANAGERS.filter(
    (m) => m.name !== caseManager && !watchers.includes(m.name),
  );

  const assign = (name: string) => {
    setMenuAnchor(null);
    if (name === caseManager) return;
    setCaseManager(name);
    // Can't own and watch — drop the new owner from watchers.
    setWatchers((w) => w.filter((n) => n !== name));
    onToast(`${name} is now the case manager`);
  };

  const addWatcher = (name: string) => {
    setWatcherAnchor(null);
    setWatchers((w) => [...w, name]);
    onToast(`${name} is now watching this circle`);
  };

  const removeWatcher = (name: string) => {
    setWatchers((w) => w.filter((n) => n !== name));
    onToast(`${name} stopped watching this circle`);
  };

  // A staff picker (case manager / watcher menus share the row layout).
  const staffMenu = (
    anchor: HTMLElement | null,
    onClose: () => void,
    options: typeof CASE_MANAGERS,
    onPick: (name: string) => void,
    selected?: string,
  ) => (
    <Menu
      anchorEl={anchor}
      open={Boolean(anchor)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      {options.map((m) => (
        <MenuItem
          key={m.name}
          selected={m.name === selected}
          onClick={() => onPick(m.name)}
        >
          <Stack
            sx={{ alignItems: 'center', minWidth: 0 }}
            direction='row'
            spacing={1.5}
          >
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: 12,
                bgcolor: 'custom.neutral.300',
                color: 'common.white',
              }}
            >
              {initials(m.name)}
            </Avatar>
            <Typography sx={{ minWidth: 0 }} variant='body2'>
              {m.name}
            </Typography>
          </Stack>
        </MenuItem>
      ))}
    </Menu>
  );

  // ── left column: who the circle is for ──────────────────────────────────

  const recipientCard = (
    <HeaderCard
      header={
        <Stack spacing={1.5}>
          <Stack
            sx={{ justifyContent: 'space-between', alignItems: 'center' }}
            direction='row'
            spacing={2}
          >
            <Stack
              sx={{ alignItems: 'center', minWidth: 0 }}
              direction='row'
              spacing={2}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'cherry.main',
                  color: 'common.white',
                }}
              >
                {initials(CARE_CIRCLE.recipient)}
              </Avatar>
              <Typography variant='body1'>{CARE_CIRCLE.recipient}</Typography>
            </Stack>
            <Button
              variant='text'
              color='primary'
              size='small'
              onClick={onEditProfile ?? mock('Editing details')}
              sx={{ flexShrink: 0 }}
            >
              Edit
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Stack direction='row' spacing={4}>
          <KeyValue label='Age' value={String(CARE_CIRCLE.age)} />
          <KeyValue label='Care stage' value={CARE_CIRCLE.careStage} />
          <KeyValue label='Funding' value={CARE_CIRCLE.funding} />
        </Stack>

        {/* Under the other facts and labelled like them. Unlabelled chips up in
            the header read as tags on the person rather than as something the
            record holds, and a CAM reading the card top to bottom met them
            before they knew whose they were. */}
        <Field label='Conditions'>
          <Stack
            sx={{ flexWrap: 'wrap', mt: 0.5 }}
            direction='row'
            spacing={1}
            useFlexGap
          >
            {CARE_CIRCLE.conditions.map((c) => (
              <Chip key={c} label={c} size='small' variant='filled' />
            ))}
          </Stack>
        </Field>

        {intake && (
          <Stack
            sx={{ pt: 1.5, borderTop: 1, borderColor: 'custom.neutral.200' }}
            spacing={0.75}
          >
            {/* Labelled like the facts above it, because that is what it is:
                one more thing the card holds. The sparkle is the member app's,
                on anything we read rather than were told.

                No Full profile action beside it: Edit up in the header opens
                the whole record, and two ways into the same drawer from one
                card is one too many. */}
            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={0.75}>
              <Box sx={{ color: 'custom.sunflower.700', display: 'flex' }}>
                <Icon icon={'sparkles' as never} fontSize={12} />
              </Box>
              <Typography variant='caption' color='text.secondary'>
                {intake === 'declined'
                  ? 'Onboarding, without AI'
                  : 'AI summary'}
              </Typography>
            </Stack>

            {intake === 'declined' ? (
              <Typography variant='body2' color='text.secondary'>
                {INTAKE.declined}
              </Typography>
            ) : (
              INTAKE.readBack.map((line) => (
                <Typography key={line} variant='body2'>
                  {line}
                </Typography>
              ))
            )}
          </Stack>
        )}
      </Stack>
    </HeaderCard>
  );

  const carersCard = (
    <HeaderCard title='Carers'>
      {/* One line, dot-separated. A carer is a name and nothing else at this
          altitude, so a stacked list gave each one a row it had no use for. */}
      <Stack
        sx={{ flexWrap: 'wrap', alignItems: 'baseline' }}
        direction='row'
        spacing={1}
        useFlexGap
      >
        {CARE_CIRCLE.carers.map((c, i) => (
          <Stack
            key={c.name}
            sx={{ alignItems: 'baseline' }}
            direction='row'
            spacing={1}
          >
            {i > 0 && (
              <Typography variant='body1' color='text.secondary'>
                ·
              </Typography>
            )}
            <Link
              component='button'
              type='button'
              variant='body1'
              onClick={() => onOpenCarer?.(c.name)}
            >
              {c.name}
            </Link>
          </Stack>
        ))}
      </Stack>
    </HeaderCard>
  );

  const upcomingCard = (
    <HeaderCard
      title='Upcoming'
      action={
        <Button
          variant='text'
          color='primary'
          size='small'
          onClick={onViewCalls ?? mock('Viewing calls')}
        >
          View calls
        </Button>
      }
    >
      {upNext ? (
        <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
          <DateChip date={upNext.date} />
          <Box sx={{ minWidth: 0 }}>
            <Link
              component='button'
              type='button'
              variant='body1'
              onClick={
                onOpenCall ? () => onOpenCall(upNext.id) : mock('Call detail')
              }
              sx={{ textAlign: 'left' }}
            >
              {upNext.title} with {upNext.withWho}
            </Link>
            <Typography variant='body2' color='text.primary'>
              {upNext.date}, {upNext.time}
              {upNext.type ? ` · ${upNext.type}` : ''}
            </Typography>
          </Box>
        </Stack>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          No upcoming calls
        </Typography>
      )}
    </HeaderCard>
  );

  // ── right column: who owns it on our side ───────────────────────────────

  // Ownership and follow in one card. They were two cards, which read as two
  // unrelated facts when the second one only means anything against the first:
  // a watcher is defined by not being the owner.
  const caseTeamCard = (
    <HeaderCard title='Case team'>
      <Stack spacing={2}>
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          direction='row'
          spacing={2}
        >
          <Stack
            sx={{ alignItems: 'center', minWidth: 0 }}
            direction='row'
            spacing={2}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: assigned ? 'cherry.main' : 'custom.neutral.300',
                color: 'common.white',
              }}
            >
              {assigned ? (
                initials(assigned.name)
              ) : (
                <Icon icon={'user' as never} fontSize={18} />
              )}
            </Avatar>
            <Typography sx={{ minWidth: 0 }} variant='body1'>
              {assigned ? assigned.name : 'Unassigned'}
            </Typography>
          </Stack>
          <Button
            variant='text'
            color='primary'
            size='small'
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ flexShrink: 0 }}
          >
            {assigned ? 'Reassign' : 'Assign'}
          </Button>
        </Stack>

        <Divider />

        <Stack spacing={1}>
          <Typography variant='body2' color='text.secondary'>
            Also watching
          </Typography>
          {/* Add sits in the row with the watchers rather than up in the
              header, because the header action manages the whole team. */}
          <Stack
            sx={{ flexWrap: 'wrap', alignItems: 'center' }}
            direction='row'
            spacing={1}
            useFlexGap
          >
            {watchers.map((w) => (
              <Chip
                key={w}
                variant='outlined'
                avatar={
                  <Avatar
                    sx={{
                      bgcolor: 'custom.neutral.300',
                      color: 'common.white',
                    }}
                  >
                    {initials(w)}
                  </Avatar>
                }
                label={w}
                onDelete={() => removeWatcher(w)}
              />
            ))}
            <AddChip
              disabled={canWatch.length === 0}
              onClick={(e) => setWatcherAnchor(e.currentTarget)}
            />
          </Stack>
        </Stack>
      </Stack>

      {staffMenu(
        menuAnchor,
        () => setMenuAnchor(null),
        CASE_MANAGERS,
        assign,
        caseManager,
      )}
      {staffMenu(
        watcherAnchor,
        () => setWatcherAnchor(null),
        canWatch,
        addWatcher,
      )}
    </HeaderCard>
  );

  const linkedCirclesCard = (
    <HeaderCard title='Linked circles'>
      {/* Name and relationship on one line, matching the carers row. What
          the two circles share was a second line nobody read here. */}
      <Stack
        sx={{ alignItems: 'baseline' }}
        direction='row'
        spacing={1}
        useFlexGap
      >
        <Link
          component='button'
          type='button'
          variant='body1'
          onClick={mock('Linked circle')}
        >
          {CARE_CIRCLE.linkedCircle}
        </Link>
        <Typography variant='body1' color='text.secondary'>
          · {CARE_CIRCLE.linkedRelationship.toLowerCase()}
        </Typography>
      </Stack>
    </HeaderCard>
  );

  return (
    <TwoColumn
      main={
        <Stack spacing={2}>
          {recipientCard}
          {upcomingCard}
        </Stack>
      }
      rail={
        <>
          {caseTeamCard}
          {carersCard}
          {linkedCirclesCard}
        </>
      }
    />
  );
};

export default OverviewTab;
